<template>
  <div v-loading.fullscreen="loading">
    <div class="header-scroll">
      <div class="header">
        <el-input
          v-model="search"
          placeholder="Mavsum nomi bo'yicha qidirish"
          clearable
          class="header-control"
          @input="onSearch"
          @clear="onSearch"
        />

        <el-select
          v-model="startYear"
          class="header-year"
          placeholder="Yildan"
          clearable
          @change="onYearChange"
        >
          <el-option
            v-for="year in yearOptions"
            :key="`s-${year}`"
            :value="year"
            :label="year"
          />
        </el-select>

        <el-select
          v-model="endYear"
          class="header-year"
          placeholder="Yilgacha"
          clearable
          @change="onYearChange"
        >
          <el-option
            v-for="year in yearOptions"
            :key="`e-${year}`"
            :value="year"
            :label="year"
            :disabled="startYear && year < startYear"
          />
        </el-select>

        <MfExcelImport
          v-if="isAdmin"
          class="header-button header-button--right"
          :action="okrugsImportUrl"
          dialog-title="Mahallalarni okruglarga Excel orqali bog'lash"
          button-text="Okruglarni import"
          button-type="warning"
          :columns="['Okrug_id', 'Quarter_id']"
          :summary-fields="[{ key: 'quarters_linked', label: 'Bog\'langan mahallalar' }]"
          template-name="okruglar-shablon.csv"
        />

        <el-button
          class="header-button"
          :class="{ 'header-button--right': !isAdmin }"
          type="success"
          :loading="downloadingExcel"
          @click="downloadAllDeputiesExcel"
        >
          <img
            src="@/assets/images/icons/download.svg"
            alt=""
            style="margin-right: 5px"
          />
          Barcha deputatlar (Excel)
        </el-button>

        <el-button
          v-if="isAdmin"
          class="header-button"
          type="info"
          @click="$router.push({ name: 'ManagerLogs' })"
        >
          Loglar
        </el-button>
      </div>
    </div>

    <el-table :data="boards" border height="calc(100vh - 270px)">
      <el-table-column type="index" :index="indexMethod" width="60" />
      <el-table-column label="Nomi" min-width="260">
        <template #default="scope">
          {{ scope.row.title
          }}{{
            scope.row.season || scope.row.year
              ? ` (${scope.row.season ? scope.row.season + "-mavsum " : ""}${
                  scope.row.year || ""
                })`
              : ""
          }}
        </template>
      </el-table-column>
      <el-table-column prop="type" label="Turi" min-width="140">
        <template #default="scope">
          {{ scope.row.type || "-" }}
        </template>
      </el-table-column>
      <el-table-column label="Deputatlar" min-width="120" align="center">
        <template #default="scope">
          {{ scope.row.deputies_count || 0 }}
        </template>
      </el-table-column>
      <el-table-column label="Biriktirish oynasi" min-width="180">
        <template #default="scope">
          <el-tag :type="windowTagType(scope.row.window_status)">
            {{ windowLabel(scope.row.window_status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Muddat" min-width="220">
        <template #default="scope">
          <!-- Manager (biriktirish) oynasi: accept_start_date → deputy_end_date -->
          {{ formatDate(scope.row.accept_start_date) }} —
          {{ formatDate(scope.row.deputy_end_date) }}
        </template>
      </el-table-column>
      <el-table-column width="160" label="Amallar" fixed="right">
        <template #default="scope">
          <el-button
            size="small"
            type="primary"
            @click="goToOkrugs(scope.row)"
          >
            Okruglar
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination">
      <div>Kengashlar soni: {{ totalCount }}</div>
      <el-pagination
        background
        layout="sizes, prev, pager, next"
        :total="totalCount"
        v-model:page-size="limit"
        v-model:current-page="currentPage"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script>
import { ElNotification } from "element-plus";
import MfExcelImport from "@/components/core/MfExcelImport.vue";
import { blobErrorMessage } from "@/utils/common";

export default {
  name: "ManagerBoards",
  components: { MfExcelImport },
  data() {
    return {
      loading: false,
      downloadingExcel: false,
      boards: [],
      totalCount: 0,
      limit: 20,
      currentPage: 1,
      search: "",
      searchTimer: null,
      // Year filter — deputy v2 era starts at 2026; pre-2026 boards must NOT appear
      // unless the user explicitly clears the default. Filters by the YEAR of
      // accept_start_date (backend-side).
      DEPUTY_V2_START_YEAR: 2026,
      startYear: 2026,
      endYear: null,
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.role === "admin";
    },
    yearOptions() {
      const current = new Date().getFullYear();
      const last = Math.max(current + 1, this.DEPUTY_V2_START_YEAR);
      const years = [];
      for (let year = last; year >= this.DEPUTY_V2_START_YEAR; year -= 1) {
        years.push(year);
      }
      return years;
    },
    okrugsImportUrl() {
      return `${process.env.VUE_APP_BASE_API_URL}api/v1/deputyv2/okrugs-sync-excel`;
    },
  },
  methods: {
    indexMethod(index) {
      return this.limit * (this.currentPage - 1) + index + 1;
    },
    formatDate(value) {
      return value ? this.$moment(value).format("DD-MM-YYYY") : "-";
    },
    windowLabel(status) {
      const map = {
        not_started: "Boshlanmagan",
        open: "Ochiq",
        closed: "Yopiq",
      };
      return map[status] || "-";
    },
    windowTagType(status) {
      const map = {
        not_started: "warning",
        open: "success",
        closed: "info",
      };
      return map[status] || "info";
    },
    getFileName(headers, fallback) {
      const contentDisposition =
        headers?.["content-disposition"] || headers?.["Content-Disposition"];
      if (!contentDisposition) return fallback;
      const part = contentDisposition
        .split(";")
        .find((item) => item.trim().startsWith("filename="));
      if (!part) return fallback;
      return decodeURIComponent(
        part.split("=")[1].trim().replace(/^"(.*)"$/, "$1")
      );
    },
    downloadAllDeputiesExcel() {
      this.downloadingExcel = true;
      this.$http
        .get("api/v1/deputyv2/deputy-manager/deputies-excel", { responseType: "blob" })
        .then((res) => {
          const url = URL.createObjectURL(
            new Blob([res.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
            })
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", this.getFileName(res.headers, "deputatlar.xlsx"));
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        })
        .catch(async ({ response }) => {
          ElNotification({
            title: "Ogohlantirish",
            message: await blobErrorMessage(
              response,
              "Excel yuklashda xatolik sodir bo'ldi!"
            ),
            type: "warning",
          });
        })
        .finally(() => {
          this.downloadingExcel = false;
        });
    },
    getBoards() {
      this.loading = true;
      this.$http
        .get("api/v1/deputyv2/deputy-manager/boards", {
          params: {
            search: this.search || null,
            limit: this.limit,
            offset: this.limit * (this.currentPage - 1),
            start_year: this.startYear || null,
            end_year: this.endYear || null,
          },
        })
        .then(({ data }) => {
          this.boards = data.boards || [];
          this.totalCount = data.count || 0;
        })
        .catch(({ response }) => {
          ElNotification({
            type: "error",
            title: "Xatolik",
            message: response?.data?.message || "Yuklashda xatolik sodir bo'ldi",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.getBoards();
      }, 400);
    },
    onYearChange() {
      if (this.startYear && this.endYear && this.endYear < this.startYear) {
        this.endYear = null;
      }
      this.currentPage = 1;
      this.getBoards();
    },
    goToOkrugs(row) {
      this.$router.push({
        name: "ManagerBoardOkrugs",
        params: { boardId: row.id },
        query: { title: row.title, year: row.year },
      });
    },
    handleCurrentChange() {
      this.getBoards();
    },
    handleSizeChange() {
      this.currentPage = 1;
      this.getBoards();
    },
  },
  mounted() {
    this.getBoards();
  },
};
</script>

<style scoped>
.header-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  margin-bottom: 10px;
}

.header {
  display: flex;
  gap: 10px;
  align-items: center;
}

.header-control {
  width: 280px;
  flex-shrink: 0;
}

.header-year {
  width: 130px;
  flex-shrink: 0;
}

.header-button {
  flex-shrink: 0;
}

.header-button--right {
  margin-left: auto;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}
</style>
